import { RetryPlugin } from '@module-federation/retry-plugin'
import { MF_PLUGIN_TELEMETRY_EVENT, type MfPluginTelemetryDetail } from './mfPluginTelemetryEvent'

// URL convention is `<host>/mfe/<mfeName>/<version>/remoteEntry.js` - extracting mfeName (not the
// versioned url) keeps mark names stable across deploys, so they aggregate in Honeycomb.
const MFE_NAME_PATTERN = /\/mfe\/([^/]+)\//
const getMfeName = (url?: string): string => (url ? MFE_NAME_PATTERN.exec(url)?.[1] : undefined) ?? 'unknown'

const dispatch = (detail: MfPluginTelemetryDetail) =>
  globalThis.dispatchEvent(new CustomEvent(MF_PLUGIN_TELEMETRY_EVENT, { detail }))

// Referenced by Module Federation's runtimePlugins in rsbuild.config.ts - retries a failed remoteEntry.js/manifest
// load (cache-busted); telemetry is a plain CustomEvent since @cesty/open-telemetry crashes this bootstrap context.
const createMfPlugins = () => ({
  ...RetryPlugin({
    addQuery: true,
    onRetry: ({ url, times }) => {
      if (times === 1) dispatch({ kind: 'retry-start', mfeName: getMfeName(url), url })
    },
    onSuccess: ({ url }) => dispatch({ kind: 'retry-outcome', mfeName: getMfeName(url), outcome: 'success', url }),
    onError: ({ url }) => dispatch({ kind: 'retry-outcome', mfeName: getMfeName(url), outcome: 'error', url }),
  }),
  afterLoadEntry: ({
    cached,
    error,
    recovered,
    remoteInfo,
  }: {
    cached?: boolean
    error?: unknown
    recovered?: boolean
    remoteInfo: { name: string }
  }) => {
    // MF 2.9 also fires this for already-loaded entries and passes the original error on recovered loads
    if (cached) return
    dispatch({
      kind: 'load-outcome',
      mfeName: remoteInfo.name,
      outcome: recovered || !error ? 'success' : 'error',
      recovered: !!recovered,
    })
  },
})

export default createMfPlugins
